import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import RefreshIcon from '@mui/icons-material/Refresh';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import { exportRowsToExcel } from '../utils/exportExcel';
import './PickListList.css';
import './Inventory.css';

const PAGE_SIZE = 50;

function sameId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function formatInvDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? String(d) : x.toLocaleString();
}

const Inventory = () => {
  const { fetchWithAuth } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const prevTrimmedSearchRef = useRef(null);

  const [selectedRow, setSelectedRow] = useState(null);
  const selectedRowId = selectedRow?.id;

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = searchInput.trim();
      if (prevTrimmedSearchRef.current !== next) {
        prevTrimmedSearchRef.current = next;
        setDebouncedSearch(next);
        setPage(1);
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const loadList = useCallback(async () => {
    try {
      setListLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        q: debouncedSearch,
      });
      const res = await fetchWithAuth(`/api/inventory?${params.toString()}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load inventory');
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      setError(err.message || 'Failed to load inventory');
      setItems([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [fetchWithAuth, page, debouncedSearch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedRowId == null) return;
    const row = items.find((r) => sameId(r.id, selectedRowId));
    if (row) setSelectedRow(row);
    else setSelectedRow(null);
  }, [items, selectedRowId]);

  const handleExportExcel = async () => {
    try {
      setError('');
      const all = [];
      let pg = 1;
      const lim = 200;
      for (;;) {
        const params = new URLSearchParams({
          page: String(pg),
          limit: String(lim),
          q: debouncedSearch,
        });
        const res = await fetchWithAuth(`/api/inventory?${params.toString()}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Export failed');
        const chunk = Array.isArray(data.items) ? data.items : [];
        all.push(...chunk);
        const tot = Number(data.total) || 0;
        if (chunk.length < lim || all.length >= tot) break;
        pg += 1;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      const rows = all.map((r) => ({
        'Part number': r.partNumber || '',
        Description: r.partDescription || '',
        Quantity: r.quantity != null ? String(r.quantity) : '',
        'Bin location': r.binLocation || '',
        Created: r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
        Updated: r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '',
      }));
      exportRowsToExcel(`inventory-${stamp}`, 'Inventory', rows);
    } catch (err) {
      setError(err.message || 'Export failed');
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRowClick = (row) => {
    setSelectedRow(row);
  };

  return (
    <Layout>
      <Box className="pick-list-md-page inventory-md-page" sx={{ p: 2 }}>
        <div className="pick-list-md-intro">
          <h1 className="home-menu-title">Inventory</h1>
        </div>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <div className="pick-list-md-toolbar">
          <Button
            type="button"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadList()}
            disabled={listLoading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={handleExportExcel}
            disabled={listLoading || total === 0}
            sx={{ textTransform: 'none' }}
          >
            ⬇ Excel
          </Button>
        </div>

        <div className="pick-list-md-split">
          <div className="pick-list-md-master">
            <div className="pick-list-md-section-header">Stock lines</div>
            <div className="pick-list-md-search">
              <TextField
                size="small"
                fullWidth
                label="Search"
                placeholder="Part, description, bin…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                margin="dense"
                sx={{ mt: 0 }}
              />
              <div className="inventory-page-hint">
                {total > 0 && (
                  <span>
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                  </span>
                )}
              </div>
            </div>
            <div className="pick-list-md-list-scroll">
              {listLoading ? (
                <p className="pick-list-md-muted">Loading…</p>
              ) : items.length === 0 ? (
                <div className="inventory-master-empty">
                  {total === 0 && !debouncedSearch ? (
                    <>
                      <div className="inventory-empty-icon" aria-hidden>
                        📦
                      </div>
                      <p className="pick-list-md-muted" style={{ marginTop: 0 }}>
                        No inventory yet. Complete receiving and putaway in the mobile app to see parts and bin locations
                        here.
                      </p>
                    </>
                  ) : (
                    <p className="pick-list-md-muted">No matches.</p>
                  )}
                </div>
              ) : (
                <table className="pick-list-md-table">
                  <thead>
                    <tr>
                      <th>Part number</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Bin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr
                        key={row.id}
                        className={sameId(selectedRow?.id, row.id) ? 'pick-list-md-row-selected' : ''}
                        onClick={() => handleRowClick(row)}
                      >
                        <td>{row.partNumber}</td>
                        <td className="inventory-md-desc-cell" title={row.partDescription}>
                          {row.partDescription || '—'}
                        </td>
                        <td>{row.quantity}</td>
                        <td>{row.binLocation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {pageCount > 1 && (
              <div className="inventory-pagination">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </div>
            )}
          </div>

          <div className="pick-list-md-detail">
            <div className="pick-list-md-section-header">
              {selectedRow ? `${selectedRow.partNumber} · ${selectedRow.binLocation || 'Bin'}` : 'Line details'}
            </div>
            {!selectedRow ? (
              <div className="pick-list-md-detail-empty">Select a stock line from the list.</div>
            ) : (
              <div className="pick-list-md-detail-body">
                <div className="pick-list-overview-grid">
                  <div className="pick-list-overview-item">
                    <span className="pick-list-overview-label">Part number</span>
                    <span className="pick-list-overview-value">{selectedRow.partNumber}</span>
                  </div>
                  <div className="pick-list-overview-item">
                    <span className="pick-list-overview-label">Quantity</span>
                    <span className="pick-list-overview-value">{selectedRow.quantity}</span>
                  </div>
                  <div className="pick-list-overview-item pick-list-overview-item--wide">
                    <span className="pick-list-overview-label">Description</span>
                    <span className="pick-list-overview-value">{selectedRow.partDescription || '—'}</span>
                  </div>
                  <div className="pick-list-overview-item">
                    <span className="pick-list-overview-label">Bin location</span>
                    <span className="pick-list-overview-value">{selectedRow.binLocation}</span>
                  </div>
                  <div className="pick-list-overview-item">
                    <span className="pick-list-overview-label">Created</span>
                    <span className="pick-list-overview-value">{formatInvDate(selectedRow.createdAt)}</span>
                  </div>
                  <div className="pick-list-overview-item">
                    <span className="pick-list-overview-label">Updated</span>
                    <span className="pick-list-overview-value">{formatInvDate(selectedRow.updatedAt)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Layout>
  );
};

export default Inventory;
